import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { teamApi, setActiveWorkspaceId } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Icon } from "@/components/ui/icon";
import { Logo } from "@/components/ui/logo";

const schema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string(),
}).refine((d) => d.password === d.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type FormData = z.infer<typeof schema>;

type InviteState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; email: string; workspaceName: string }
  | { status: "accepted" };

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = !!user;

  const [invite, setInvite] = useState<InviteState>({ status: "loading" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (!token) {
      setInvite({ status: "error", message: "Invalid invitation link." });
      return;
    }
    teamApi.getInvitation(token).then((res) => {
      if ("error" in res) {
        setInvite({ status: "error", message: res.error?.message ?? "Invitation not found." });
      } else if (res.data) {
        setInvite({ status: "ready", email: res.data.email, workspaceName: res.data.workspaceName });
      }
    });
  }, [token]);

  // One-click accept for already-logged-in users
  const handleAcceptLoggedIn = async () => {
    if (!token) return;
    setIsSubmitting(true);
    const res = await teamApi.acceptInvitationLoggedIn(token);
    setIsSubmitting(false);

    if ("error" in res) {
      toast.error(res.error?.message ?? "Failed to accept invitation.");
      return;
    }

    if (res.data) {
      // HttpOnly cookie is set by backend — no need to store token in localStorage
      setActiveWorkspaceId(res.data.workspace.id);
      toast.success(`You've joined ${res.data.workspace.name}!`);
      window.location.href = "/dashboard";
    }
  };

  // Signup + accept for new users
  const onSubmit = async (data: FormData) => {
    if (!token) return;
    setIsSubmitting(true);
    const res = await teamApi.acceptInvitation(token, data.name, data.password);
    setIsSubmitting(false);

    if ("error" in res) {
      if (res.error?.code === "409") {
        toast.error("An account with this email already exists. Please log in.");
        navigate("/login");
      } else {
        toast.error(res.error?.message ?? "Failed to accept invitation.");
      }
      return;
    }

    if (res.data) {
      // HttpOnly cookie is set by backend — no need to store token in localStorage
      setActiveWorkspaceId(res.data.workspace.id);
      toast.success(`Welcome to ${res.data.workspace.name}!`);
      window.location.href = "/dashboard";
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">

        {/* Logo */}
        <div className="flex justify-center pb-2">
          <Logo variant="full" height={28} />
        </div>

        {/* Loading */}
        {invite.status === "loading" && (
          <div className="flex justify-center py-8">
            <Icon name="refresh" size={20} className="animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Error */}
        {invite.status === "error" && (
          <div className="space-y-4 text-center">
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-4">
              <p className="font-sans text-sm text-destructive">{invite.message}</p>
            </div>
            <p className="font-sans text-xs text-muted-foreground">
              The invitation may have expired or already been used.
            </p>
            <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>
              Go to login
            </Button>
          </div>
        )}

        {/* Ready — logged in: one-click join */}
        {invite.status === "ready" && isLoggedIn && (
          <div className="space-y-6">
            <div className="space-y-1 text-center">
              <h1 className="font-mono text-base font-semibold tracking-tight">
                Join {invite.workspaceName}
              </h1>
              <p className="font-sans text-xs text-muted-foreground">
                You're logged in as{" "}
                <span className="text-foreground font-medium">{user?.email}</span>.
              </p>
              {user?.email?.toLowerCase() !== invite.email.toLowerCase() && (
                <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-left">
                  <p className="font-sans text-xs text-amber-400">
                    This invite was sent to <span className="font-medium">{invite.email}</span>.
                    You're logged in as a different account. Log out and sign in with the correct email to accept.
                  </p>
                </div>
              )}
            </div>

            {user?.email?.toLowerCase() === invite.email.toLowerCase() ? (
              <Button
                className="w-full"
                onClick={handleAcceptLoggedIn}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? <Icon name="refresh" size={12} className="mr-2 animate-spin" />
                  : <Icon name="user-add-01" size={12} className="mr-2" />
                }
                Join {invite.workspaceName}
              </Button>
            ) : (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/login")}
              >
                Log out and switch account
              </Button>
            )}
          </div>
        )}

        {/* Ready — not logged in: signup form */}
        {invite.status === "ready" && !isLoggedIn && (
          <>
            <div className="space-y-1 text-center">
              <h1 className="font-mono text-base font-semibold tracking-tight">
                Join {invite.workspaceName}
              </h1>
              <p className="font-sans text-xs text-muted-foreground">
                You've been invited as{" "}
                <span className="text-foreground">{invite.email}</span>
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  Your name
                </Label>
                <Input id="name" type="text" placeholder="Jane Smith" {...register("name")} />
                {errors.name && <p className="font-sans text-xs text-destructive">{errors.name.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  Password
                </Label>
                <Input id="password" type="password" placeholder="••••••••" {...register("password")} />
                {errors.password && <p className="font-sans text-xs text-destructive">{errors.password.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword" className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  Confirm password
                </Label>
                <Input id="confirmPassword" type="password" placeholder="••••••••" {...register("confirmPassword")} />
                {errors.confirmPassword && <p className="font-sans text-xs text-destructive">{errors.confirmPassword.message}</p>}
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting && <Icon name="refresh" size={12} className="mr-2 animate-spin" />}
                Accept invitation
              </Button>
            </form>

            <p className="text-center font-sans text-xs text-muted-foreground">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="text-foreground hover:underline font-medium"
              >
                Sign in
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
